import React, { useState, useEffect } from 'react';
import { getVaticanPosts, pinVaticanPost, triggerVaticanFetch, likePost } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { MembershipBanner } from '../components/MembershipModal';
import { useSEO } from '../hooks/useSEO';
import { PostCard } from './DashboardPage';
import { 
  ExternalLink, 
  Pin, 
  RefreshCw, 
  Video, 
  Newspaper,
  ChevronDown,
  Globe
} from 'lucide-react';

// Vatican News YouTube Channels by Language
const VATICAN_CHANNELS = {
  en: { id: "UCxIsefyl9g9A5SGWA4FvGIA", name: "English" },
  es: { id: "UCnB5vfb9FMMNTnC6-kAT3fQ", name: "Español" },
  it: { id: "UC7E-LYc1wivk33iyt5bR5zQ", name: "Italiano" },
  // Additional languages can be added as their channel IDs are discovered
  // pt: { id: "...", name: "Português" },
  // fr: { id: "...", name: "Français" },
  // de: { id: "...", name: "Deutsch" },
};

// Country to language mapping
const COUNTRY_TO_LANGUAGE = {
  // English
  US: 'en', GB: 'en', AU: 'en', CA: 'en', IE: 'en', NZ: 'en', ZA: 'en', PH: 'en', IN: 'en', NG: 'en', KE: 'en', GH: 'en',
  // Spanish
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', CL: 'es', VE: 'es', EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', PR: 'es',
  // Italian
  IT: 'it', SM: 'it', VA: 'it',
  // Portuguese (fallback to Spanish for now until PT channel ID is found)
  BR: 'es', PT: 'es', AO: 'es', MZ: 'es',
  // French (fallback to English for now)
  FR: 'en', BE: 'en', CH: 'en', LU: 'en', MC: 'en', SN: 'en', CI: 'en', CM: 'en', MG: 'en', ML: 'en', BF: 'en', NE: 'en', TG: 'en', BJ: 'en', GA: 'en', CG: 'en', CD: 'en', HT: 'en',
  // German (fallback to English for now)
  DE: 'en', AT: 'en', LI: 'en',
};

// Default language if country not found
const DEFAULT_LANGUAGE = 'en';

// Get YouTube embed URL for a channel
const getYouTubeEmbedUrl = (channelId) => {
  return `https://www.youtube.com/embed/videoseries?list=UU${channelId.substring(2)}`;
};

export default function VaticanNewsPage({ user, isPublic = false }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [detectedLanguage, setDetectedLanguage] = useState(DEFAULT_LANGUAGE);
  const [detectingLocation, setDetectingLocation] = useState(true);
  const limit = 10;

  // SEO
  useSEO({
    title: "Vatican News",
    description: "Stay updated with the latest news from the Vatican and Pope Francis. Read official announcements, homilies, and Catholic news from the Holy See.",
    path: "/vatican-news"
  });

  const isAdmin = user?.role === 'admin' || user?.is_site_admin;
  const isParishAdmin = user?.role === 'parish_admin' || user?.is_parish_admin || isAdmin;

  // Detect user's location and set appropriate language
  useEffect(() => {
    const detectLocation = async () => {
      try {
        // Use free IP geolocation API
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code;
          const language = COUNTRY_TO_LANGUAGE[countryCode] || DEFAULT_LANGUAGE;
          
          // Only set if we have a channel for this language
          if (VATICAN_CHANNELS[language]) {
            setDetectedLanguage(language);
          }
        }
      } catch (err) {
      } finally {
        setDetectingLocation(false);
      }
    };
    
    detectLocation();
  }, []);

  const loadPosts = async (loadMore = false) => {
    try {
      const currentSkip = loadMore ? skip : 0;
      const res = await getVaticanPosts({ limit, skip: currentSkip });
      if (loadMore) {
        setPosts(prev => [...prev, ...res.data.posts]);
      } else {
        setPosts(res.data.posts);
      }
      setHasMore(res.data.has_more);
      setSkip(currentSkip + limit);
    } catch (err) {
      toast.error('Failed to load Vatican News');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManualFetch = async () => {
    setFetchLoading(true);
    try {
      const res = await triggerVaticanFetch();
      toast.success(`Fetched ${res.data.created} new posts`);
      loadPosts();
    } catch (err) {
      toast.error('Failed to fetch Vatican News');
    } finally {
      setFetchLoading(false);
    }
  };

  const handlePinPost = async (postId) => {
    try {
      const res = await pinVaticanPost(postId);
      toast.success(res.data.message);
      // Update local state
      setPosts(prev => prev.map(p => 
        p.post_id === postId 
          ? { ...p, is_pinned: res.data.pinned }
          : p
      ));
    } catch (err) {
      toast.error('Failed to pin post');
    }
  };

  const handleLike = async (postId) => {
    try {
      const res = await likePost(postId);
      setPosts(prev => prev.map(p => p.post_id === postId ? {
        ...p,
        user_liked: res.data.liked,
        like_count: res.data.like_count
      } : p));
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="vatican-news-page">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B7355] to-[#6B5344] text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://www.vaticannews.va/etc/designs/vatican-news/release/library/main/images/stemma-leo-blank.png" 
                alt="Vatican" 
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold font-['Playfair_Display']">Vatican News</h1>
                <p className="text-white/80 text-sm font-['Nunito']">Latest from the Holy See</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualFetch}
                  disabled={fetchLoading}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="vatican-fetch-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${fetchLoading ? 'animate-spin' : ''}`} />
                  Fetch Now
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Public Access Banner */}
        {isPublic && (
          <MembershipBanner context="vatican" className="mb-6" />
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video Widget */}
          <div className="lg:col-span-2">
            <Card className="bg-white border-[#E5E0D5] overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-lg font-['Playfair_Display']">
                  <div className="flex items-center gap-2">
                    <Video className="w-5 h-5 text-[#8B7355]" />
                    Vatican News Videos
                  </div>
                  {!detectingLocation && VATICAN_CHANNELS[detectedLanguage] && (
                    <Badge variant="outline" className="text-xs font-['Nunito'] border-[#8B7355]/30 text-[#8B7355]">
                      <Globe className="w-3 h-3 mr-1" />
                      {VATICAN_CHANNELS[detectedLanguage].name}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  {detectingLocation ? (
                    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-[#F9F7F2]">
                      <div className="text-center">
                        <RefreshCw className="w-8 h-8 text-[#8B7355] animate-spin mx-auto mb-2" />
                        <p className="text-sm text-[#6B5344]/60 font-['Nunito']">Detecting your location...</p>
                      </div>
                    </div>
                  ) : (
                    <iframe 
                      src={getYouTubeEmbedUrl(VATICAN_CHANNELS[detectedLanguage]?.id || VATICAN_CHANNELS.en.id)}
                      className="absolute top-0 left-0 w-full h-full"
                      style={{ borderRadius: '0 0 8px 8px' }}
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={`Vatican News Videos (${VATICAN_CHANNELS[detectedLanguage]?.name || 'English'})`}
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Posts */}
            <div className="mt-8">
              <h2 className="text-xl font-bold font-['Playfair_Display'] mb-4 flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-[#8B7355]" />
                Recent Vatican News
              </h2>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <Card key={i} className="bg-white border-[#E5E0D5]">
                      <CardContent className="p-4">
                        <Skeleton className="h-4 w-3/4 mb-2" />
                        <Skeleton className="h-3 w-full mb-1" />
                        <Skeleton className="h-3 w-2/3" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <Card className="bg-white border-[#E5E0D5]">
                  <CardContent className="p-8 text-center">
                    <Globe className="w-12 h-12 mx-auto text-[#8B7355]/30 mb-4" />
                    <p className="text-[#6B5344]/60 font-['Nunito']">No Vatican News posts yet.</p>
                    {isAdmin && (
                      <Button 
                        className="mt-4 bg-[#8B7355] hover:bg-[#6B5344]"
                        onClick={handleManualFetch}
                      >
                        Fetch Now
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
              <div className="space-y-4">
                  {posts.map(post => (
                    <PostCard
                      key={post.post_id}
                      post={post}
                      currentUserId={user?.user_id}
                      onLike={handleLike}
                      onComment={() => {}}
                    />
                  ))}

                  {hasMore && (
                    <Button
                      variant="outline"
                      className="w-full border-[#E5E0D5] text-[#6B5344]"
                      onClick={() => loadPosts(true)}
                    >
                      <ChevronDown className="w-4 h-4 mr-2" />
                      Load More
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* About */}
            <Card className="bg-white border-[#E5E0D5]">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-['Playfair_Display']">About</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#6B5344]/80 font-['Nunito'] leading-relaxed">
                  Stay connected with the universal Church through Vatican News. Articles are automatically 
                  fetched from the official Vatican News feed every hour, bringing you the latest on papal 
                  activities, Church teachings, and global Catholic news. Select any post to read the full 
                  article on vaticannews.va.
                </p>
                <div className="mt-4 p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]">
                  <p className="text-xs text-[#6B5344]/60 font-['Nunito'] italic">
                    "This initiative by Vatican News is a tool for evangelisation, offering an opportunity 
                    to create a network and exchange gifts between Rome and the Church in your country."
                  </p>
                  <p className="text-xs text-[#8B7355] mt-2 font-semibold">— Leo XIV</p>
                </div>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="bg-white border-[#E5E0D5]">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-['Playfair_Display']">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <a 
                  href="https://www.vaticannews.va/en.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-[#8B7355] hover:text-[#6B5344] font-['Nunito']"
                >
                  <ExternalLink className="w-4 h-4" />
                  Vatican News Website
                </a>
                <a 
                  href="https://www.vatican.va" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-[#8B7355] hover:text-[#6B5344] font-['Nunito']"
                >
                  <ExternalLink className="w-4 h-4" />
                  Vatican Official Site
                </a>
                <a 
                  href="https://www.vaticannews.va/en/epg.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-[#8B7355] hover:text-[#6B5344] font-['Nunito']"
                >
                  <ExternalLink className="w-4 h-4" />
                  Live Programming Guide
                </a>
              </CardContent>
            </Card>

            {/* Push Notification Info */}
            <Card className="bg-gradient-to-br from-[#8B7355]/10 to-[#D4AF37]/10 border-[#E5E0D5]">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🔔</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C1810] font-['Playfair_Display'] text-sm">
                      Stay Notified
                    </h3>
                    <p className="text-xs text-[#6B5344]/70 font-['Nunito'] mt-1">
                      Enable push notifications to receive alerts for major papal events, 
                      encyclicals, and important Church announcements.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
